import { useMapData } from '@app/contexts/mapData.context';

/**
 * Hook that provides access to cached map data and utility functions
 * This is a convenience hook that wraps the MapDataContext
 */
export const useMapCache = () => {
  const { 
    state, 
    loadGoogleMaps, 
    loadDashboardData, 
    invalidateCache, 
    clearCache,
    addSiteToCache,
    removeSiteFromCache,
    addControllerToCache,
    removeControllerFromCache,
    updateSiteInCache,
    updateControllerInCache
  } = useMapData();

  return {
    // Loading states
    isGoogleMapsLoaded: state.googleMapsLoaded,
    isGoogleMapsLoading: state.googleMapsLoading,
    isDashboardLoading: state.dashboardLoading,
    
    // Error states
    googleMapsError: state.googleMapsError,
    dashboardError: state.dashboardError,
    
    // Data
    sites: state.sites || [],
    controllers: state.controllers || [],
    dashboardData: state.dashboardData,
    
    // Cache info (4-hour cache)
    lastFetched: state.lastFetched,
    lastStatusCheck: state.lastStatusCheck,
    isCacheExpired: state.lastFetched ? (Date.now() - state.lastFetched) > (4 * 60 * 60 * 1000) : true,
    isStatusStale: state.lastStatusCheck ? (Date.now() - state.lastStatusCheck) > (5 * 60 * 1000) : true,
    
    // Cache age helpers
    cacheAgeMinutes: state.lastFetched ? Math.floor((Date.now() - state.lastFetched) / 60000) : null,
    statusAgeMinutes: state.lastStatusCheck ? Math.floor((Date.now() - state.lastStatusCheck) / 60000) : null,
    
    // Actions
    loadMaps: loadGoogleMaps,
    loadData: loadDashboardData,
    refreshData: () => loadDashboardData(true), // Force refresh
    invalidateCache, // For full refresh when needed
    clearCache,
    
    // Direct cache CRUD operations (zero API cost!)
    addSiteToCache,
    removeSiteFromCache,
    addControllerToCache,
    removeControllerFromCache,
    updateSiteInCache,
    updateControllerInCache,
    
    // Computed helpers
    totalSites: state.dashboardData?.partner?.sites?.count || 0,
    totalControllers: state.dashboardData?.partner?.controllers?.count || 0,
    totalWans: state.dashboardData?.partner?.wans || 0,
    
    // Status helpers
    siteAlerts: (state.sites || []).filter(site => site.status === 1 || site.status === 2 || site.status === 4),
    controllerAlerts: (state.controllers || []).filter(controller => controller.status === 1 || controller.status === 2 || controller.status === 4),
    gatewayAlerts: (state.dashboardData?.partner?.gateways?.gateways || []).filter(gateway => gateway.status === 1 || gateway.status === 2 || gateway.status === 4),
  };
};

export default useMapCache; 